/**
 * Phase 1 SMB Modal Analytics Tracking
 *
 * Google Analytics (GTM) tracking for the SMBSignupModal component.
 * Tracks user interactions on expert profile pages with utm_source=govtribe/highergov.
 *
 * Events tracked:
 * - Modal lifecycle (open/close)
 * - Signup form interactions (links, buttons, form submission)
 * - Login form interactions (links, buttons, form submission)
 * - Success/error outcomes
 *
 * All events include:
 * - user_status (new/returning_with_cookie/logged_in)
 * - UTM parameters (if present in URL)
 * - Expert context (name, ID)
 * - Landing URL
 */
import {
  type UserStatus,
  calculateDuration,
  captureUTMParams,
  getUserStatus,
} from '@/auth/utils/analyticsHelpers';
import { gtm } from '@/core/analytics';

import { ExpertContextData } from '../types';

export { calculateDuration, captureUTMParams, getUserStatus };
export type { UserStatus };

/**
 * Current view in the modal (signup or login form)
 */
type ViewType = 'signup' | 'login';

/**
 * Error types for tracking
 */
type ErrorType = 'validation' | 'api' | 'network' | 'unknown';

/**
 * Login step (credentials entry or MFA verification)
 */
type LoginStep = 'credentials' | 'mfa';

/**
 * Common event parameters included in all tracking events
 */
interface BaseEventParams {
  url: string;
  expert_name?: string;
  expert_id?: string;
  user_status: UserStatus;
  timestamp: string;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Extract expert ID from URL
 * Expert profile URLs: /profile/:expertSlug or /profiles/:expertSlug
 *
 * @returns Expert ID/slug from URL, or undefined if not found
 */
export function getExpertIdFromURL(): string | undefined {
  const pathParts = window.location.pathname.split('/');
  const profileIndex = pathParts.findIndex((part) => part === 'profile' || part === 'profiles');

  if (profileIndex !== -1 && pathParts[profileIndex + 1]) {
    return pathParts[profileIndex + 1];
  }

  return undefined;
}

/**
 * Get base event parameters common to all tracking events
 *
 * @param expert - Expert context data (optional)
 * @returns Base parameters object
 */
function getBaseParams(expert?: ExpertContextData): BaseEventParams {
  return {
    url: window.location.href,
    expert_name: expert?.name,
    expert_id: getExpertIdFromURL(),
    user_status: getUserStatus(),
    timestamp: new Date().toISOString(),
  };
}

// ============================================================================
// MODAL LIFECYCLE EVENTS
// ============================================================================

/**
 * Track modal opened event
 * Fired when Phase 1 modal opens on expert profile page
 *
 * @param expert - Expert context data
 */
export function trackPhase1ModalOpened(expert?: ExpertContextData): void {
  const params = {
    event: 'smb_phase1_modal_opened',
    ...getBaseParams(expert),
    ...captureUTMParams(),
  };

  gtm('event', 'smb_phase1_modal_opened', params);
}

/**
 * Track modal closed event
 * Fired when user navigates away or closes browser
 * (Modal is non-dismissable, so this fires on unload)
 *
 * @param currentView - Current view when modal closed (signup/login)
 * @param startTime - When modal was opened (for duration calculation)
 * @param expert - Expert context data
 */
export function trackPhase1ModalClosed(
  currentView: ViewType,
  startTime: string | Date,
  expert?: ExpertContextData
): void {
  const params = {
    event: 'smb_phase1_modal_closed',
    ...getBaseParams(expert),
    current_view: currentView,
    duration_on_modal: calculateDuration(startTime),
  };

  gtm('event', 'smb_phase1_modal_closed', params);
}

// ============================================================================
// SIGNUP FORM EVENTS (SMBSignupForm.tsx)
// ============================================================================

/**
 * Track Privacy Policy link click
 *
 * @param expert - Expert context data
 */
export function trackPhase1PrivacyPolicyClicked(expert?: ExpertContextData): void {
  const params = {
    event: 'smb_phase1_privacy_policy_clicked',
    ...getBaseParams(expert),
  };

  gtm('event', 'smb_phase1_privacy_policy_clicked', params);
}

/**
 * Track Terms of Use link click
 *
 * @param expert - Expert context data
 */
export function trackPhase1TermsOfUseClicked(expert?: ExpertContextData): void {
  const params = {
    event: 'smb_phase1_terms_of_use_clicked',
    ...getBaseParams(expert),
  };

  gtm('event', 'smb_phase1_terms_of_use_clicked', params);
}

/**
 * Track "Sign in" link click (toggle to login view)
 *
 * @param timeOnSignupView - Time spent on signup view before clicking
 * @param expert - Expert context data
 */
export function trackPhase1SwitchToLoginClicked(
  timeOnSignupView: number,
  expert?: ExpertContextData
): void {
  const params = {
    event: 'smb_phase1_switch_to_login_clicked',
    ...getBaseParams(expert),
    time_on_signup_view: timeOnSignupView,
  };

  gtm('event', 'smb_phase1_switch_to_login_clicked', params);
}

/**
 * Track "Get Free Access" button click (form submit)
 *
 * @param hasFilledAllFields - Whether all required fields are filled
 * @param formDuration - Time spent filling out the form
 * @param expert - Expert context data
 */
export function trackPhase1GetFreeAccessClicked(
  hasFilledAllFields: boolean,
  formDuration: number,
  expert?: ExpertContextData
): void {
  const params = {
    event: 'smb_phase1_get_free_access_clicked',
    ...getBaseParams(expert),
    has_filled_all_fields: hasFilledAllFields,
    form_duration: formDuration,
  };

  gtm('event', 'smb_phase1_get_free_access_clicked', params);
}

/**
 * Track successful signup
 *
 * @param durationToComplete - Total time from modal open to signup success
 * @param expert - Expert context data
 */
export function trackPhase1SignupSuccess(
  durationToComplete: number,
  expert?: ExpertContextData
): void {
  const params = {
    event: 'smb_phase1_signup_success',
    ...getBaseParams(expert),
    ...captureUTMParams(),
    duration_to_complete: durationToComplete,
  };

  gtm('event', 'smb_phase1_signup_success', params);
}

/**
 * Track signup failure
 *
 * @param errorType - Type of error (validation/api/network)
 * @param errorMessage - Error message (user-friendly)
 * @param errorCode - Error code (if available)
 * @param expert - Expert context data
 */
export function trackPhase1SignupFailed(
  errorType: ErrorType,
  errorMessage: string,
  errorCode?: string,
  expert?: ExpertContextData
): void {
  const params = {
    event: 'smb_phase1_signup_failed',
    ...getBaseParams(expert),
    error_type: errorType,
    error_message: errorMessage,
    ...(errorCode && { error_code: errorCode }),
  };

  gtm('event', 'smb_phase1_signup_failed', params);
}

// ============================================================================
// LOGIN FORM EVENTS (SMBLoginForm.tsx)
// ============================================================================

/**
 * Track "Sign up" link click (toggle to signup view)
 *
 * @param timeOnLoginView - Time spent on login view before clicking
 * @param expert - Expert context data
 */
export function trackPhase1SwitchToSignupClicked(
  timeOnLoginView: number,
  expert?: ExpertContextData
): void {
  const params = {
    event: 'smb_phase1_switch_to_signup_clicked',
    ...getBaseParams(expert),
    time_on_login_view: timeOnLoginView,
  };

  gtm('event', 'smb_phase1_switch_to_signup_clicked', params);
}

/**
 * Track "Forgot your password?" link click
 *
 * @param expert - Expert context data
 */
export function trackPhase1ForgotPasswordClicked(expert?: ExpertContextData): void {
  const params = {
    event: 'smb_phase1_forgot_password_clicked',
    ...getBaseParams(expert),
  };

  gtm('event', 'smb_phase1_forgot_password_clicked', params);
}

/**
 * Track "Log In" button click (form submit)
 *
 * @param has2FA - Whether 2FA/MFA step was shown
 * @param expert - Expert context data
 */
export function trackPhase1LoginSubmitted(has2FA: boolean, expert?: ExpertContextData): void {
  const params = {
    event: 'smb_phase1_login_submitted',
    ...getBaseParams(expert),
    has_2fa: has2FA,
  };

  gtm('event', 'smb_phase1_login_submitted', params);
}

/**
 * Track successful login
 *
 * @param had2FA - Whether 2FA was used
 * @param durationToComplete - Total time from modal open to login success
 * @param expert - Expert context data
 */
export function trackPhase1LoginSuccess(
  had2FA: boolean,
  durationToComplete: number,
  expert?: ExpertContextData
): void {
  const params = {
    event: 'smb_phase1_login_success',
    ...getBaseParams(expert),
    had_2fa: had2FA,
    duration_to_complete: durationToComplete,
  };

  gtm('event', 'smb_phase1_login_success', params);
}

/**
 * Track login failure
 *
 * @param errorType - Type of error (invalid_credentials/locked_account/mfa_failed)
 * @param errorCode - Error code (if available)
 * @param loginStep - Which step failed (credentials/mfa)
 * @param expert - Expert context data
 */
export function trackPhase1LoginFailed(
  errorType: string,
  errorCode: string | undefined,
  loginStep: LoginStep,
  expert?: ExpertContextData
): void {
  const params = {
    event: 'smb_phase1_login_failed',
    ...getBaseParams(expert),
    error_type: errorType,
    login_step: loginStep,
    ...(errorCode && { error_code: errorCode }),
  };

  gtm('event', 'smb_phase1_login_failed', params);
}
